import { LinkType } from 'constants/LinkType';
import ICompanyInvitationLink from 'types/employee/ICompanyInvitationLink';
import { IJobApplicantLink } from 'types/employee/IJobApplicantLink';
import { ICompanyLink } from '../types';

/** Stable composite row id. Exported so callers can key off the same rule the grid uses. */
export const buildRowId = (linkType: LinkType, id: number): string => `${linkType}-${id}`;

/**
 * Normalise an onboarding link (CompanyOnboarding) into a grid row.
 *
 * Onboarding links express "every branch" as an `isForAllLocations` flag with an empty
 * location list, so the label has to come from a translated string rather than from names.
 * `uniqueIdentifier` is already a full URL server-side.
 */
const mapOnboardingLink = (link: ICompanyInvitationLink, allLocationsLabel: string): ICompanyLink => ({
  rowId: buildRowId(LinkType.Onboarding, link.id),
  id: link.id,
  linkType: LinkType.Onboarding,
  shareUrl: link.uniqueIdentifier ?? '',
  branchesLabel: link.isForAllLocations ? allLocationsLabel : (link.locations ?? []).join(', '),
  canDelete: true,
});

/**
 * Normalise an application link (JobApplicants) into a grid row.
 *
 * Application links always carry explicit branches — the API has no "all branches" flag,
 * because "select all" is expanded into ids at creation time so the link's reach never
 * changes when the company gains a branch later. A link created via the toggle therefore
 * renders as a full name list, not as the "all locations" label.
 */
const mapApplicationLink = (link: IJobApplicantLink): ICompanyLink => ({
  rowId: buildRowId(LinkType.Application, link.id),
  id: link.id,
  linkType: LinkType.Application,
  shareUrl: link.url,
  branchesLabel: (link.branches ?? []).map(branch => branch.name).join(', '),
  canDelete: link.canDelete,
});

/**
 * Merge both link sources into one grid dataset.
 *
 * `allLocationsLabel` is passed in already translated, keeping this function pure and
 * testable — it must not reach for a `t` of its own.
 */
const mapCompanyLinks = (
  onboardingLinks: ICompanyInvitationLink[],
  applicationLinks: IJobApplicantLink[],
  allLocationsLabel: string,
): ICompanyLink[] => [
  ...(onboardingLinks ?? []).map(link => mapOnboardingLink(link, allLocationsLabel)),
  ...(applicationLinks ?? []).map(mapApplicationLink),
];

export default mapCompanyLinks;
